import { z } from "zod";

import { influenceScopeSchema } from "./contact.js";

export const territoryRelationshipStatusSchema = z.enum([
  "confirmed",
  "former",
]);
export type TerritoryRelationshipStatus = z.infer<
  typeof territoryRelationshipStatusSchema
>;

export const contactOrganizationalUnitAffiliationRoleSchema = z.enum([
  "faculty",
  "staff",
  "administrator",
  "purchasing_support",
  "vendor_setup",
  "other",
  "unspecified",
]);
export type ContactOrganizationalUnitAffiliationRole = z.infer<
  typeof contactOrganizationalUnitAffiliationRoleSchema
>;

export const contactLabScopeResolutionStatusSchema = z.enum([
  "not_asserted",
  "resolved",
  "unresolved_legacy",
]);
export type ContactLabScopeResolutionStatus = z.infer<
  typeof contactLabScopeResolutionStatusSchema
>;

export const contactLabScopeTargetInputShape = {
  influenceScope: influenceScopeSchema.nullable().default(null),
  influenceScopeOrganizationalUnitId: z.uuid().optional(),
  influenceScopeLocationId: z.uuid().optional(),
};

export function validateContactLabScopeTargetInput(
  value: {
    readonly influenceScope: z.infer<typeof influenceScopeSchema> | null;
    readonly influenceScopeOrganizationalUnitId?: string | undefined;
    readonly influenceScopeLocationId?: string | undefined;
  },
  context: z.RefinementCtx,
): void {
  const hasUnit = value.influenceScopeOrganizationalUnitId !== undefined;
  const hasLocation = value.influenceScopeLocationId !== undefined;

  if (value.influenceScope === "department") {
    if (!hasUnit) {
      context.addIssue({
        code: "custom",
        path: ["influenceScopeOrganizationalUnitId"],
        message:
          "Department scope requires the canonical OrganizationalUnit ID.",
      });
    }
    if (hasLocation) {
      context.addIssue({
        code: "custom",
        path: ["influenceScopeLocationId"],
        message: "Department scope cannot target a Location.",
      });
    }
    return;
  }

  if (
    value.influenceScope === "building" ||
    value.influenceScope === "campus"
  ) {
    if (!hasLocation) {
      context.addIssue({
        code: "custom",
        path: ["influenceScopeLocationId"],
        message: `${value.influenceScope} scope requires the canonical Location ID.`,
      });
    }
    if (hasUnit) {
      context.addIssue({
        code: "custom",
        path: ["influenceScopeOrganizationalUnitId"],
        message: `${value.influenceScope} scope cannot target an OrganizationalUnit.`,
      });
    }
    return;
  }

  if (hasUnit || hasLocation) {
    context.addIssue({
      code: "custom",
      path: [
        hasUnit
          ? "influenceScopeOrganizationalUnitId"
          : "influenceScopeLocationId",
      ],
      message: `${value.influenceScope} scope uses its Contact-Lab endpoint and does not accept an additional target ID.`,
    });
  }
}

export interface ContactLabScopeTargetIdentity {
  readonly entityType:
    | "lab"
    | "organization"
    | "organizational_unit"
    | "location";
  readonly id: string;
  readonly name: string;
  readonly type: string | null;
}
